import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Layers, Award, Clock, Headset, HeartHandshake, ArrowRight } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { Reveal } from "@/components/Reveal";
import { CONTACT, IMAGES, SERVICE_GROUPS, KEUNGGULAN, PROYEK } from "@/data/company";

const ICONS = { Users, Layers, Award, Clock, Headset, HeartHandshake };

const Marquee = () => {
  const items = SERVICE_GROUPS.map((g) => g.title);
  const row = [...items, ...items, ...items];
  return (
    <div data-testid="services-marquee" className="border-y border-white/10 bg-navy-900 py-5 overflow-hidden" aria-hidden="true">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {[0, 1].map((half) => (
          <div key={half} className="flex">
            {row.map((t, i) => (
              <span key={`${half}-${i}`} className="flex items-center gap-6 px-6 text-sm tracking-[0.2em] uppercase text-slate-400">
                {t}
                <span className="w-1.5 h-1.5 rotate-45 bg-gold inline-block" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Home() {
  return (
    <>
      <SEOHead
        title="PT. Luminara Global Persada | Company Profile Konstruksi, Arsitektur, Interior & Perdagangan"
        description="PT. Luminara Global Persada — perusahaan konstruksi, arsitektur, desain interior, lanskap & perdagangan besar di Depok, Jawa Barat. Unggul, terpercaya, tepat waktu."
      />

      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Gedung pencakar langit modern — PT. Luminara Global Persada perusahaan konstruksi dan arsitektur"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#060B19] via-[#060B19]/85 to-[#060B19]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060B19] via-transparent to-transparent" />

        <div className="container-site relative z-10 pt-32 pb-24">
          <motion.p
            className="kicker mb-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Company Profile Resmi
          </motion.p>
          <motion.h1
            data-testid="hero-heading"
            className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white max-w-3xl leading-[1.05]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            PT. Luminara <span className="text-gold">Global Persada</span>
          </motion.h1>
          <motion.p
            className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Membangun masa depan, merancang keunggulan. PT. Luminara Global Persada adalah perusahaan
            yang bergerak di bidang konstruksi, perdagangan, arsitektur, interior, dan lanskap —
            berorientasi pada kualitas, ketepatan waktu, integritas, dan solusi terpadu.
          </motion.p>
          <motion.div
            className="mt-10 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <Link to="/kontak" data-testid="hero-cta-contact" className="btn-gold">
              Hubungi Kami
            </Link>
            <Link to="/layanan" data-testid="hero-cta-services" className="btn-outline">
              Lihat Layanan
            </Link>
            <Link to="/tentang" data-testid="hero-cta-profile" className="btn-outline">
              Profil Perusahaan
            </Link>
          </motion.div>

          <motion.div
            data-testid="hero-stats"
            className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {[
              { n: "19", l: "Bidang Usaha Terdaftar" },
              { n: "5", l: "Kategori Layanan Utama" },
              { n: "6", l: "Tahap Proses Kerja Terukur" },
            ].map((s) => (
              <div key={s.l}>
                <p className="font-heading text-3xl md:text-4xl font-semibold text-gold">{s.n}</p>
                <p className="mt-1 text-xs md:text-sm text-slate-400">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section className="section-pad" data-testid="keunggulan-section">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Keunggulan Perusahaan</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Standar kerja yang menjaga kepercayaan klien dan mitra
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {KEUNGGULAN.map((k, i) => {
              const Icon = ICONS[k.icon];
              return (
                <Reveal key={k.title} delay={i * 0.08}>
                  <div
                    data-testid={`keunggulan-card-${i}`}
                    className="h-full bg-navy-900 border border-white/10 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50"
                  >
                    <Icon size={28} strokeWidth={1.5} className="text-gold" />
                    <h3 className="mt-5 font-heading font-semibold text-lg text-white">{k.title}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{k.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad bg-navy-900 border-y border-white/10" data-testid="layanan-preview-section">
        <div className="container-site">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="kicker mb-4">Bidang Usaha</p>
              <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-xl">
                Lima kategori layanan, satu standar mutu
              </h2>
            </div>
            <Link to="/layanan" data-testid="layanan-preview-link" className="btn-outline shrink-0">
              Semua Layanan <ArrowRight size={16} />
            </Link>
          </Reveal>
          <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
            {SERVICE_GROUPS.map((g, i) => (
              <Reveal key={g.id} delay={i * 0.06}>
                <Link
                  to={`/layanan#${g.id}`}
                  data-testid={`layanan-preview-${g.id}`}
                  className="group grid md:grid-cols-12 gap-4 items-center py-8 transition-colors duration-300 hover:bg-navy-800/60 md:px-6"
                >
                  <span className="md:col-span-1 font-heading text-2xl text-gold/50 group-hover:text-gold transition-colors duration-300">
                    0{i + 1}
                  </span>
                  <h3 className="md:col-span-4 font-heading font-semibold text-xl md:text-2xl text-white">
                    {g.title}
                  </h3>
                  <p className="md:col-span-6 text-sm text-slate-400 leading-relaxed">{g.desc}</p>
                  <ArrowRight size={20} className="md:col-span-1 text-slate-500 group-hover:text-gold group-hover:translate-x-1 transition-all duration-300" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="tentang-preview-section">
        <div className="container-site grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <img
                src={IMAGES.architectsBlueprint}
                alt="Tim arsitek PT. Luminara Global Persada meninjau blueprint proyek konstruksi"
                className="w-full aspect-[4/3] object-cover border border-white/10"
              />
              <div className="absolute -bottom-6 -right-6 w-40 h-40 border border-gold/40 hidden md:block" aria-hidden="true" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="kicker mb-4">Tentang Perusahaan</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight">
              Mitra terpercaya untuk kebutuhan pembangunan dan pengadaan Anda
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">
              PT. Luminara Global Persada berdiri dengan komitmen menghadirkan layanan konstruksi,
              perdagangan, arsitektur, interior, dan lanskap yang profesional. Kami percaya setiap
              proyek adalah amanah — karena itu kualitas, ketepatan waktu, dan integritas menjadi
              dasar setiap pekerjaan yang kami tangani.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/tentang" data-testid="tentang-preview-cta" className="btn-gold">
                Profil Perusahaan
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-navy-900 border-y border-white/10" data-testid="proyek-preview-section">
        <div className="container-site">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="kicker mb-4">Portofolio</p>
              <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-xl">
                Cakupan pekerjaan yang kami layani
              </h2>
            </div>
            <Link to="/proyek" data-testid="proyek-preview-link" className="btn-outline shrink-0">
              Lihat Portofolio <ArrowRight size={16} />
            </Link>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {PROYEK.slice(0, 3).map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div data-testid={`proyek-preview-card-${i}`} className="group bg-navy-800 border border-white/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-gold/50">
                  <div className="overflow-hidden">
                    <img
                      src={p.image}
                      alt={`${p.name} — portofolio PT. Luminara Global Persada di ${p.location}`}
                      className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-xs tracking-widest uppercase text-gold">{p.location}</p>
                    <h3 className="mt-2 font-heading font-semibold text-lg text-white">{p.name}</h3>
                    <p className="mt-2 text-sm text-slate-400">{p.type}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="cta-banner-section">
        <div className="container-site">
          <Reveal>
            <div className="relative border border-gold/30 bg-navy-800 px-8 py-16 md:p-20 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gold" aria-hidden="true" />
              <div className="max-w-2xl">
                <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight">
                  Diskusikan kebutuhan proyek Anda bersama kami
                </h2>
                <p className="mt-5 text-slate-400 leading-relaxed">
                  Tim PT. Luminara Global Persada siap membantu dari tahap konsultasi awal hingga
                  serah terima pekerjaan. Hubungi kami melalui formulir kontak, email, atau WhatsApp.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to="/kontak" data-testid="cta-banner-contact" className="btn-gold">
                    Hubungi Kami
                  </Link>
                  <a
                    href={CONTACT.waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="cta-banner-whatsapp"
                    className="btn-outline"
                  >
                    WhatsApp Kami
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
